// src/features/data/use-client-sort.ts
// IR-1045 Phase 1: shared controlled-sort helper for the client-side Data
// Management tables (orphans, blacklist, avatars) whose hooks return the full
// row array (no server pagination). Wires the DataTable onSort/sortKey/
// sortDirection contract to an in-memory comparator with stable nulls-last
// ordering. Server-paginated tabs (groups, entities) sort via query params
// instead and do NOT use this.
import type { SortDirection } from "@/components/iris";
import { useState } from "react";

export type SortAccessor<T> = (row: T) => string | number | null | undefined;

export interface ClientSort<T> {
  sorted: T[];
  sortKey: string | null;
  sortDirection: SortDirection;
  onSort: (key: string, direction: SortDirection) => void;
}

/**
 * Controlled client-side sort over `rows`.
 *
 * @param rows      Full row list (already filtered).
 * @param accessors Map of column key → value accessor used for comparison.
 * @param defaultSort Optional initial sort applied until the user clicks a header.
 * @param tiebreakers Optional accessors applied, in order, only when the active
 *   column compares equal. Always ASCENDING, whichever way the column is
 *   pointing — see the note on the comparator below.
 */
export function useClientSort<T>(
  rows: T[],
  accessors: Record<string, SortAccessor<T>>,
  defaultSort?: { key: string; direction: Exclude<SortDirection, null> },
  tiebreakers?: SortAccessor<T>[],
): ClientSort<T> {
  const [sortKey, setSortKey] = useState<string | null>(defaultSort?.key ?? null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSort?.direction ?? null);

  const onSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction);
  };

  // Extracted so the tiebreakers below compare by exactly the same rules as the
  // primary column — including nulls-last, which a hand-rolled second compare
  // would have been free to get wrong.
  const compare = (
    av: ReturnType<SortAccessor<T>>,
    bv: ReturnType<SortAccessor<T>>,
    dir: number,
  ) => {
    // Nulls always sort last, regardless of direction.
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  };

  let sorted = rows;
  const accessor = sortKey ? accessors[sortKey] : undefined;
  if (sortKey && sortDirection && accessor) {
    const dir = sortDirection === "asc" ? 1 : -1;
    sorted = [...rows].sort((a, b) => {
      const primary = compare(accessor(a), accessor(b), dir);
      if (primary !== 0) return primary;
      // IR-1474: tiebreakers stay ASCENDING even when the column is descending.
      // They are a presentation rule about what a tied block looks like ("per
      // each source, A-Z"), not part of the ordering the user chose by clicking
      // — reversing them would make one descending click on Status silently
      // reverse the alphabet inside every status block, which is not what was
      // asked for and reads as a bug.
      for (const tiebreak of tiebreakers ?? []) {
        const next = compare(tiebreak(a), tiebreak(b), 1);
        if (next !== 0) return next;
      }
      return 0;
    });
  }

  return { sorted, sortKey, sortDirection, onSort };
}
